/**
 * CDISC ODM-XML Importer
 * Parses CDISC ODM v1.3.x metadata (FormDef / ItemGroupDef / ItemDef / CodeList)
 * and converts each <FormDef> into a `trial_protocol_forms`-compatible payload.
 *
 * Understands REDCap's `redcap:` extension attributes (FieldType, Identifier,
 * FieldNote, SectionHeader, TextValidationType, BranchingLogic, CheckboxChoices)
 * so a REDCap ODM export round-trips into faithful field types instead of
 * collapsing everything to text/select.
 *
 * Pure function — no Supabase calls, no React, no side effects. Safe to unit-test
 * and to run in offline / PWA contexts.
 */

export type OdmFieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'radio'
  | 'checkbox'
  | 'select'
  | 'textarea'
  | 'scale';

export interface OdmFormField {
  id: string;
  type: OdmFieldType;
  label: string;
  required: boolean;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  placeholder?: string;
  /** Original ODM OID — preserved for round-tripping and SDTM mapping. */
  odm_item_oid?: string;
  /** SDTM target (e.g. "DM.AGE") if present in the ODM Alias. */
  sdtm_target?: string;
  /** True when REDCap flagged the field as an identifier (redcap:Identifier="y"). */
  pii?: boolean;
  /** REDCap field note shown under the input. */
  help_text?: string;
  /** REDCap section header that precedes this field. */
  section_header?: string;
  /** REDCap branching logic expression — stored for traceability only. */
  branching_logic?: string;
  /** Original REDCap variable name (checkbox groups collapse to one variable). */
  redcap_variable?: string;
  /** Original REDCap field type (checkbox, radio, yesno, file, notes, …). */
  redcap_field_type?: string;
  /** REDCap validation type (date_dmy, integer, email, phone, …). */
  redcap_validation?: string;
}

export interface OdmFormImport {
  formName: string;
  formDescription?: string;
  formType: 'crf' | 'adverse_event' | 'concomitant_medication' | 'protocol_deviation' | 'informed_consent';
  version: string;
  schema: {
    formTitle: string;
    formDescription?: string;
    fields: OdmFormField[];
  };
  /** Original FormDef OID — preserved for traceability. */
  odm_form_oid: string;
}

export interface OdmImportResult {
  studyOid?: string;
  studyName?: string;
  metaDataVersion?: string;
  /** ODM AsOfDateTime / CreationDateTime — used as the imported form version. */
  sourceDateTime?: string;
  /** Source system reported by the export (e.g. "REDCap"). */
  sourceSystem?: string;
  forms: OdmFormImport[];
  warnings: string[];
}


/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function attr(el: Element, name: string): string | undefined {
  // ODM uses unprefixed attributes; querySelector handles namespace-stripped DOM.
  const v = el.getAttribute(name);
  return v === null ? undefined : v;
}

function textOf(el: Element | null | undefined): string | undefined {
  if (!el) return undefined;
  const t = (el.textContent || '').trim();
  return t.length ? t : undefined;
}

function inferFormType(name: string): OdmFormImport['formType'] {
  const n = name.toLowerCase();
  if (n.includes('adverse') || /\bae\b/.test(n)) return 'adverse_event';
  if (n.includes('concomitant') || /\bcm\b/.test(n)) return 'concomitant_medication';
  if (n.includes('deviation')) return 'protocol_deviation';
  if (n.includes('consent')) return 'informed_consent';
  return 'crf';
}

function mapOdmDataType(
  dataType: string | undefined,
  hasCodeList: boolean,
  length: number | undefined,
): OdmFieldType {
  if (hasCodeList) return 'select';
  switch ((dataType || '').toLowerCase()) {
    case 'integer':
    case 'float':
    case 'double':
    case 'decimal':
      return 'number';
    case 'date':
    case 'datetime':
    case 'time':
    case 'partialdate':
    case 'partialdatetime':
    case 'partialtime':
    case 'incompletedate':
    case 'incompletedatetime':
      return 'date';
    case 'boolean':
      return 'radio';
    case 'text':
    case 'string':
    default:
      return length && length > 200 ? 'textarea' : 'text';
  }
}

/* ------------------------------------------------------------------ */
/* REDCap extension helpers                                            */
/* ------------------------------------------------------------------ */

/** Reads a namespaced attribute, tolerating a missing/renamed prefix. */
function nsAttr(el: Element | null | undefined, local: string): string | undefined {
  if (!el) return undefined;
  const direct = el.getAttribute(`redcap:${local}`) ?? el.getAttribute(local);
  if (direct) return direct;
  for (let i = 0; i < el.attributes.length; i++) {
    const a = el.attributes[i];
    if (a.localName === local || a.name.endsWith(`:${local}`)) return a.value;
  }
  return undefined;
}

/** Parses redcap:CheckboxChoices — "1, Label one|2, Label two". */
function parseCheckboxChoices(raw?: string): { value: string; label: string }[] {
  if (!raw) return [];
  return raw
    .split('|')
    .map((chunk) => {
      const idx = chunk.indexOf(',');
      if (idx < 0) return null;
      const value = chunk.slice(0, idx).trim();
      const label = chunk.slice(idx + 1).trim();
      return value ? { value, label: label || value } : null;
    })
    .filter(Boolean) as { value: string; label: string }[];
}

/**
 * Maps a REDCap field type onto the platform's renderer types.
 * `file` and `descriptive` have no direct equivalent; they degrade to text and
 * keep `redcap_field_type` so nothing about the original intent is lost.
 */
function mapRedcapFieldType(
  redcapType: string | undefined,
  hasOptions: boolean,
  fallback: OdmFieldType,
): OdmFieldType {
  switch ((redcapType || '').toLowerCase()) {
    case 'checkbox':
      return 'checkbox';
    case 'radio':
    case 'yesno':
    case 'truefalse':
      return 'radio';
    case 'dropdown':
    case 'select':
      return 'select';
    case 'notes':
      return 'textarea';
    case 'slider':
      return 'scale';
    case 'calc':
      return 'number';
    case 'file':
    case 'descriptive':
    case 'text':
      // REDCap free-text fields carry Length="999" by default, which the generic
      // ODM mapper would misread as a long-text area. Keep them single-line.
      return hasOptions ? 'select' : 'text';
    default:
      return fallback;
  }
}

/** REDCap validation types that imply a numeric or date input. */
function refineByValidation(type: OdmFieldType, validation?: string): OdmFieldType {
  const v = (validation || '').toLowerCase();
  if (!v) return type;
  if (v.startsWith('date') || v.startsWith('datetime')) return 'date';
  if (v === 'integer' || v === 'number' || v.startsWith('number_')) return 'number';
  return type;
}

/* ------------------------------------------------------------------ */
/* Main parser                                                         */
/* ------------------------------------------------------------------ */


export function parseOdmXml(xml: string): OdmImportResult {
  const warnings: string[] = [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');

  const parserError = doc.getElementsByTagName('parsererror')[0];
  if (parserError) {
    throw new Error(`Invalid ODM-XML: ${parserError.textContent?.slice(0, 200) || 'parse error'}`);
  }

  const odmRoot = doc.documentElement;
  if (!odmRoot || !/odm/i.test(odmRoot.localName)) {
    throw new Error('Root element is not <ODM>. Expected a CDISC ODM v1.3 document.');
  }

  // getElementsByTagName ignores namespace prefixes, which is what we want
  // because real-world ODM exports use varying prefixes (odm:, def:, none).
  const studyEl = odmRoot.getElementsByTagName('Study')[0];
  if (!studyEl) {
    throw new Error('No <Study> element found in ODM document.');
  }

  const studyOid = attr(studyEl, 'OID');
  const globalVarsEl = studyEl.getElementsByTagName('GlobalVariables')[0];
  const studyName = textOf(globalVarsEl?.getElementsByTagName('StudyName')[0]);

  const metaDataVersionEl = studyEl.getElementsByTagName('MetaDataVersion')[0];
  if (!metaDataVersionEl) {
    throw new Error('No <MetaDataVersion> element found in <Study>.');
  }
  const metaDataVersion =
    attr(metaDataVersionEl, 'Name') || attr(metaDataVersionEl, 'OID') || '1.0';

  const sourceDateTime =
    attr(odmRoot, 'AsOfDateTime') || attr(odmRoot, 'CreationDateTime') || undefined;
  const sourceSystem = attr(odmRoot, 'SourceSystem') || undefined;

  /* ---------- Build lookup tables ---------- */

  // CodeLists: OID -> { items: [{ codedValue, decode }] }
  const codeLists = new Map<string, { value: string; label: string }[]>();
  // REDCap checkbox groups publish their real option labels on the CodeList.
  const checkboxChoices = new Map<string, { value: string; label: string }[]>();
  const codeListEls = metaDataVersionEl.getElementsByTagName('CodeList');
  for (let i = 0; i < codeListEls.length; i++) {
    const cl = codeListEls[i];
    const oid = attr(cl, 'OID');
    if (!oid) continue;
    const items: { value: string; label: string }[] = [];

    // CodeListItem (with Decode/TranslatedText) OR EnumeratedItem
    const cli = cl.getElementsByTagName('CodeListItem');
    for (let j = 0; j < cli.length; j++) {
      const item = cli[j];
      const value = attr(item, 'CodedValue') || '';
      const decode = item.getElementsByTagName('Decode')[0];
      const tt = decode?.getElementsByTagName('TranslatedText')[0];
      items.push({ value, label: textOf(tt) || value });
    }
    const enumItems = cl.getElementsByTagName('EnumeratedItem');
    for (let j = 0; j < enumItems.length; j++) {
      const item = enumItems[j];
      const value = attr(item, 'CodedValue') || '';
      items.push({ value, label: value });
    }
    if (items.length) codeLists.set(oid, items);

    const choices = parseCheckboxChoices(nsAttr(cl, 'CheckboxChoices'));
    if (choices.length) checkboxChoices.set(oid, choices);
  }

  // ItemDefs: OID -> definition
  interface ItemDefInfo {
    name: string;
    dataType?: string;
    length?: number;
    question?: string;
    codeListOid?: string;
    sdtmTarget?: string;
    redcapVariable?: string;
    redcapFieldType?: string;
    redcapValidation?: string;
    fieldNote?: string;
    sectionHeader?: string;
    branchingLogic?: string;
    identifier?: boolean;
  }

  const itemDefs = new Map<string, ItemDefInfo>();
  const itemDefEls = metaDataVersionEl.getElementsByTagName('ItemDef');
  for (let i = 0; i < itemDefEls.length; i++) {
    const it = itemDefEls[i];
    const oid = attr(it, 'OID');
    if (!oid) continue;
    const lengthAttr = attr(it, 'Length');
    const codeListRef = it.getElementsByTagName('CodeListRef')[0];
    const questionEl = it.getElementsByTagName('Question')[0];
    const questionTT = questionEl?.getElementsByTagName('TranslatedText')[0];

    // SDTM target via Alias Context="SDTM"
    let sdtmTarget: string | undefined;
    const aliases = it.getElementsByTagName('Alias');
    for (let a = 0; a < aliases.length; a++) {
      if ((attr(aliases[a], 'Context') || '').toUpperCase() === 'SDTM') {
        sdtmTarget = attr(aliases[a], 'Name');
        break;
      }
    }

    itemDefs.set(oid, {
      name: attr(it, 'Name') || oid,
      dataType: attr(it, 'DataType'),
      length: lengthAttr ? Number(lengthAttr) : undefined,
      question: textOf(questionTT) || attr(it, 'Name'),
      codeListOid: codeListRef ? attr(codeListRef, 'CodeListOID') : undefined,
      sdtmTarget,
      redcapVariable: nsAttr(it, 'Variable'),
      redcapFieldType: nsAttr(it, 'FieldType'),
      redcapValidation: nsAttr(it, 'TextValidationType'),
      fieldNote: nsAttr(it, 'FieldNote'),
      sectionHeader: nsAttr(it, 'SectionHeader'),
      branchingLogic: nsAttr(it, 'BranchingLogic'),
      identifier: (nsAttr(it, 'Identifier') || '').toLowerCase() === 'y',
    });
  }


  // ItemGroupDefs: OID -> ordered list of { itemOid, mandatory }
  const itemGroupDefs = new Map<string, { itemOid: string; mandatory: boolean; orderNumber: number }[]>();
  const igEls = metaDataVersionEl.getElementsByTagName('ItemGroupDef');
  for (let i = 0; i < igEls.length; i++) {
    const ig = igEls[i];
    const oid = attr(ig, 'OID');
    if (!oid) continue;
    const refs: { itemOid: string; mandatory: boolean; orderNumber: number }[] = [];
    const itemRefs = ig.getElementsByTagName('ItemRef');
    for (let j = 0; j < itemRefs.length; j++) {
      const ref = itemRefs[j];
      const itemOid = attr(ref, 'ItemOID');
      if (!itemOid) continue;
      refs.push({
        itemOid,
        mandatory: (attr(ref, 'Mandatory') || '').toLowerCase() === 'yes',
        orderNumber: Number(attr(ref, 'OrderNumber') || j + 1),
      });
    }
    refs.sort((a, b) => a.orderNumber - b.orderNumber);
    itemGroupDefs.set(oid, refs);
  }

  /* ---------- Build OdmFormImport list ---------- */

  const forms: OdmFormImport[] = [];
  const formDefEls = metaDataVersionEl.getElementsByTagName('FormDef');
  for (let i = 0; i < formDefEls.length; i++) {
    const fd = formDefEls[i];
    const formOid = attr(fd, 'OID') || `FORM_${i}`;
    const formName = attr(fd, 'Name') || formOid;
    const repeating = (attr(fd, 'Repeating') || '').toLowerCase() === 'yes';

    const fields: OdmFormField[] = [];
    // REDCap explodes one checkbox question into <var>___1, <var>___2, … ItemDefs.
    // Collapse them back into a single multi-select field keyed by the variable.
    const checkboxSeen = new Set<string>();
    const groupRefs = fd.getElementsByTagName('ItemGroupRef');
    for (let g = 0; g < groupRefs.length; g++) {
      const groupOid = attr(groupRefs[g], 'ItemGroupOID');
      if (!groupOid) continue;
      const items = itemGroupDefs.get(groupOid) || [];
      for (const ref of items) {
        const def = itemDefs.get(ref.itemOid);
        if (!def) {
          warnings.push(`Form "${formName}": item ${ref.itemOid} referenced but not defined.`);
          continue;
        }
        const isCheckbox = (def.redcapFieldType || '').toLowerCase() === 'checkbox';
        const variable = def.redcapVariable || def.name;

        if (isCheckbox) {
          if (checkboxSeen.has(variable)) continue;
          checkboxSeen.add(variable);
          const choices = def.codeListOid ? checkboxChoices.get(def.codeListOid) : undefined;
          if (!choices?.length) {
            warnings.push(
              `Form "${formName}": checkbox "${variable}" had no readable choices — options left empty.`,
            );
          }
          fields.push({
            id: `field_${variable}`,
            type: 'checkbox',
            label: def.question || variable,
            required: ref.mandatory,
            options: choices?.length ? choices : undefined,
            odm_item_oid: variable,
            sdtm_target: def.sdtmTarget,
            pii: def.identifier || undefined,
            help_text: def.fieldNote || undefined,
            section_header: def.sectionHeader || undefined,
            branching_logic: def.branchingLogic || undefined,
            redcap_variable: variable,
            redcap_field_type: 'checkbox',
          });
          continue;
        }

        const codeListItems = def.codeListOid ? codeLists.get(def.codeListOid) : undefined;
        const baseType = mapOdmDataType(def.dataType, !!codeListItems?.length, def.length);
        const fieldType = refineByValidation(
          mapRedcapFieldType(def.redcapFieldType, !!codeListItems?.length, baseType),
          def.redcapValidation,
        );
        const isFile = (def.redcapFieldType || '').toLowerCase() === 'file';

        fields.push({
          id: `field_${ref.itemOid}`,
          type: fieldType,
          label: def.question || def.name,
          required: ref.mandatory,
          options: codeListItems && codeListItems.length ? codeListItems : undefined,
          odm_item_oid: ref.itemOid,
          sdtm_target: def.sdtmTarget,
          pii: def.identifier || undefined,
          help_text: isFile
            ? [def.fieldNote, 'Uploaded document reference (file upload in source system).']
                .filter(Boolean)
                .join(' — ')
            : def.fieldNote || undefined,
          section_header: def.sectionHeader || undefined,
          branching_logic: def.branchingLogic || undefined,
          redcap_variable: def.redcapVariable || undefined,
          redcap_field_type: def.redcapFieldType || undefined,
          redcap_validation: def.redcapValidation || undefined,
        });
      }
    }


    if (fields.length === 0) {
      warnings.push(`Form "${formName}" has no resolvable fields — skipped.`);
      continue;
    }

    forms.push({
      formName,
      formDescription: repeating ? 'Repeating form (per ODM definition)' : undefined,
      formType: inferFormType(formName),
      version: '1.0',
      odm_form_oid: formOid,
      schema: {
        formTitle: formName,
        formDescription: repeating ? 'Repeating form (per ODM definition)' : undefined,
        fields,
      },
    });
  }

  if (forms.length === 0) {
    throw new Error('ODM document parsed successfully but contained no <FormDef> elements with resolvable fields.');
  }

  return {
    studyOid,
    studyName,
    metaDataVersion,
    sourceDateTime,
    sourceSystem,
    forms,
    warnings,
  };

}
